import type { MessageDescriptor } from 'react-intl';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { BodyM, HeadlineXL } from '@sorare/core/src/atoms/typography';
import { Dialog } from '@sorare/core/src/components/dialog';
import { OneTimeDialog } from '@sorare/core/src/contexts/oneTimeDialog/Provider';
import type { LIFECYCLE } from '@sorare/core/src/hooks/useLifecycle';
import { glossary } from '@sorare/core/src/lib/glossary';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

const Cover = styled.div<{ img: string; imgDesktop: string }>`
  background-position: top center;
  background-size: contain;
  background-repeat: no-repeat;
  padding-top: 56.25%;
  @media ${tabletAndAbove} {
    padding-top: 0;
    aspect-ratio: 2;
    background-size: cover;
  }
`;

const Content = styled.div`
  padding: var(--triple-unit);
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: var(--double-unit);

  > *:last-child {
    margin-top: var(--double-unit);
    @media ${tabletAndAbove} {
      align-self: center;
    }
  }
`;

const CloseButtonWrapper = styled.div`
  position: absolute;
  z-index: 1;
  top: var(--triple-unit);
  right: var(--triple-unit);
`;

const OnboardingDialog = ({
  title,
  description,
  img,
  imgDesktop = img,
  open,
  onClose,
}: {
  title: MessageDescriptor;
  description: MessageDescriptor;
  img: string;
  imgDesktop?: string;
  open: boolean;
  onClose: () => void;
}) => {
  return (
    <Dialog open={open} maxWidth="sm" hideHeader onClose={onClose}>
      {({ CloseButton }) => (
        <>
          <CloseButtonWrapper>
            <CloseButton onClose={onClose} />
          </CloseButtonWrapper>
          <Cover
            img={img}
            imgDesktop={imgDesktop}
            style={{
              backgroundImage: `url(${img})`,
            }}
          />
          <Content>
            <HeadlineXL as="h2">
              <FormattedMessage {...title} />
            </HeadlineXL>
            <BodyM color="var(--c-nd-400)">
              <FormattedMessage {...description} />
            </BodyM>
            <Button size="medium" color="primary" onClick={onClose}>
              <FormattedMessage {...glossary.discover} />
            </Button>
          </Content>
        </>
      )}
    </Dialog>
  );
};

export const OneTimeOnboardingDialog = ({
  lifecycleKey,
  show,
  ...rest
}: {
  title: MessageDescriptor;
  description: MessageDescriptor;
  img: string;
  imgDesktop?: string;
  lifecycleKey: LIFECYCLE;
  show: boolean;
}) => {
  return (
    <OneTimeDialog dialogId={lifecycleKey} show={show}>
      {({ onClose, open }) => (
        <OnboardingDialog onClose={onClose} open={open} {...rest} />
      )}
    </OneTimeDialog>
  );
};
